import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/devLogger';
import { FamilyInvitation, FamilyRole } from '../types';
import { requestDeduplicator } from '../lib/requestCache';
import { withTimeout, isTimeoutError } from '../lib/queryUtils';
import { useFamilyGroupStore } from './familyGroupStore';

interface FamilyInvitationState {
  // Count of *received* (incoming) invitations the current user has not yet
  // accepted/dismissed. Set by PendingInvitationsBanner after its RPC fetch
  // and read by usePromptOrchestrator to give those banners exclusive
  // attention on the dashboard. -1 means "not yet loaded".
  receivedCount: number;
  setReceivedCount: (count: number) => void;

  inviteMember: (familyGroupId: string, email: string, role: FamilyRole, invitationType?: 'family_member' | 'protagonist', storyId?: string) => Promise<{ success: boolean; error?: string; invitationId?: string }>;
  fetchPendingInvitations: (familyGroupId: string) => Promise<FamilyInvitation[]>;
  cancelInvitation: (invitationId: string) => Promise<{ success: boolean; error?: string }>;
  acceptInvitation: (token: string) => Promise<{ success: boolean; error?: string; familyGroupId?: string }>;
  getInvitationByToken: (token: string) => Promise<{ invitation: FamilyInvitation | null; reason: string | null }>;
}

export const useFamilyInvitationStore = create<FamilyInvitationState>((set, get) => ({
  receivedCount: -1,
  setReceivedCount: (count: number) => set({ receivedCount: count }),


  inviteMember: async (familyGroupId: string, email: string, role: FamilyRole, invitationType: 'family_member' | 'protagonist' = 'family_member', storyId?: string) => {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      console.log('[FamilyInvitationStore] inviteMember called:', { familyGroupId, email: normalizedEmail, role, invitationType, storyId });
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      // Check total pending invitations for this family (limit: 10)
      const { count: pendingCount, error: countError } = await supabase
        .from('family_invitations')
        .select('*', { count: 'exact', head: true })
        .eq('family_group_id', familyGroupId)
        .eq('status', 'pending');

      if (countError) {
        console.error('[FamilyInvitationStore] Error counting invitations:', countError);
      } else if (pendingCount && pendingCount >= 10) {
        return { success: false, error: 'Límite de 10 invitaciones pendientes alcanzado. Cancela alguna invitación existente para poder enviar una nueva.' };
      }

      // Check if user with this email is already a member
      const { data: existingUserProfile } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name')
        .eq('email', normalizedEmail)
        .maybeSingle();

      if (existingUserProfile) {
        const { data: existingMember } = await supabase
          .from('family_members')
          .select('id, role')
          .eq('family_group_id', familyGroupId)
          .eq('user_id', existingUserProfile.id)
          .maybeSingle();

        if (existingMember) {
          const userName = existingUserProfile.first_name && existingUserProfile.last_name
            ? `${existingUserProfile.first_name} ${existingUserProfile.last_name}`
            : normalizedEmail;
          return {
            success: false,
            error: `${userName} ya es miembro de esta familia con rol de ${existingMember.role}`
          };
        }
      }

      // Check if there's already a pending, non-expired invitation for this email.
      // PRO-353: expired-but-still-marked-pending rows must not block a re-send —
      // the status column is never auto-flipped to 'expired', so we filter by
      // expires_at > now() to only block invitations that are actually live.
      const { data: existingInvitation } = await supabase
        .from('family_invitations')
        .select('id, created_at')
        .eq('family_group_id', familyGroupId)
        .eq('email', normalizedEmail)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .maybeSingle();

      if (existingInvitation) {
        return { success: false, error: 'Ya existe una invitación pendiente para este correo. Puedes cancelarla desde la pestaña de Invitaciones Pendientes si deseas enviar una nueva.' };
      }

      const userExists = !!existingUserProfile;

      // Create invitation
      const { data: invitation, error } = await supabase
        .from('family_invitations')
        .insert([{
          family_group_id: familyGroupId,
          email: normalizedEmail,
          role,
          invited_by: user.id,
          user_exists: userExists,
          invitation_type: invitationType,
          story_id: storyId ?? null,
        }])
        .select()
        .single();

      if (error) throw error;

      // Send invitation email via Edge Function
      console.log('[FamilyInvitationStore] Sending invitation email via Edge Function...');
      try {
        const functionUrl = `${supabase.supabaseUrl}/functions/v1/send-family-invitation`;
        const { data: { session } } = await supabase.auth.getSession();

        const emailResponse = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            invitationId: invitation.id,
          }),
        });

        if (!emailResponse.ok) {
          const errorData = await emailResponse.json();
          console.error('[FamilyInvitationStore] Error sending invitation email:', errorData);
          if (emailResponse.status === 429) {
            console.warn('[FamilyInvitationStore] Rate limit reached for invitations:', errorData.error);
          }
          console.warn('[FamilyInvitationStore] Invitation created but email failed to send');
        } else {
          console.log('[FamilyInvitationStore] Invitation email sent successfully');
        }
      } catch (emailError: any) {
        console.error('[FamilyInvitationStore] Exception sending invitation email:', emailError);
        console.warn('[FamilyInvitationStore] Invitation created but email failed to send');
      }

      // Invalidate relevant caches
      requestDeduplicator.invalidateCachePattern(familyGroupId);
      requestDeduplicator.invalidateCachePattern('fetchPendingInvitations');

      console.log('[FamilyInvitationStore] Invitation sent successfully:', invitation.id);

      // Refresh pending invitations list
      await get().fetchPendingInvitations(familyGroupId);

      return { success: true, invitationId: invitation.id };
    } catch (error: any) {
      console.error('Error inviting member:', error);
      return { success: false, error: error.message || 'Error al enviar invitación' };
    }
  },

  fetchPendingInvitations: async (familyGroupId: string) => {
    try {
      console.log('[FamilyInvitationStore] Fetching pending invitations for family:', familyGroupId);

      const { data, error } = await withTimeout(
        supabase.rpc('get_family_invitations', { p_family_group_id: familyGroupId }),
        10000,
        'Loading invitations timed out'
      );

      if (error) {
        console.error('[FamilyInvitationStore] Error fetching invitations:', error);
        throw error;
      }

      const invitations = (data || []).map((inv: any) => ({
        id: inv.id,
        family_group_id: inv.family_group_id,
        email: inv.email,
        role: inv.role,
        invited_by: inv.invited_by,
        token: inv.token,
        expires_at: inv.expires_at,
        status: 'pending' as const,
        created_at: inv.created_at || new Date().toISOString(),
        invited_by_profile: {
          email: inv.email,
          first_name: inv.invited_by_name?.split(' ')[0] || '',
          last_name: inv.invited_by_name?.split(' ').slice(1).join(' ') || '',
          full_name: inv.invited_by_name || 'Someone'
        }
      }));

      console.log('[FamilyInvitationStore] Found', invitations.length, 'pending invitations');
      return invitations;
    } catch (error: any) {
      console.error('[FamilyInvitationStore] Error in fetchPendingInvitations:', error);

      if (isTimeoutError(error)) {
        return [];
      }

      throw error;
    }
  },

  cancelInvitation: async (invitationId: string) => {
    try {
      console.log('[cancelInvitation] Calling RPC function for invitation:', invitationId);

      const { data, error } = await supabase.rpc('cancel_family_invitation', {
        invitation_id_param: invitationId
      });

      if (error) {
        console.error('[cancelInvitation] RPC error:', error);
        throw error;
      }

      if (data && !data.success) {
        console.error('[cancelInvitation] Function returned error:', data.error);
        return { success: false, error: data.error };
      }

      console.log('[cancelInvitation] ✓ Invitation cancelled successfully');

      // Refresh invitations for the relevant family
      const { data: invitation } = await supabase
        .from('family_invitations')
        .select('family_group_id')
        .eq('id', invitationId)
        .maybeSingle();

      if (invitation) {
        await get().fetchPendingInvitations(invitation.family_group_id);
      }

      return { success: true };
    } catch (error: any) {
      console.error('[cancelInvitation] Error cancelling invitation:', error);
      return { success: false, error: error.message || 'Failed to cancel invitation' };
    }
  },

  acceptInvitation: async (token: string) => {
    try {
      console.log('[FamilyInvitationStore] acceptInvitation called');
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      const result = await get().getInvitationByToken(token);

      if (!result.invitation) {
        return { success: false, error: 'Invitation not found' };
      }

      const invitation = result.invitation;

      // Verify family group still exists
      console.log('[acceptInvitation] Verifying family group exists (with invited user RLS)');
      const { data: familyGroup, error: groupError } = await supabase
        .from('family_groups')
        .select('id, name')
        .eq('id', invitation.family_group_id)
        .maybeSingle();

      if (groupError) {
        console.error('[acceptInvitation] ❌ Error querying family group:', groupError);
        if (groupError.code === 'PGRST116' || groupError.message?.includes('permission denied')) {
          return {
            success: false,
            error: 'PERMISSION_DENIED',
            message: 'No tienes permiso para ver este grupo familiar. Verifica que la invitación sea válida.'
          };
        }
        return {
          success: false,
          error: 'QUERY_ERROR',
          message: 'Error al verificar el grupo familiar. Intenta de nuevo.'
        };
      }

      if (!familyGroup) {
        console.error('[acceptInvitation] ❌ Family group not found or was deleted');
        return {
          success: false,
          error: 'GROUP_DELETED',
          message: 'El grupo familiar al que fuiste invitado ya no existe'
        };
      }

      console.log('[acceptInvitation] ✅ Family group verified:', familyGroup.name);

      if (invitation.status !== 'pending') {
        return { success: false, error: 'Invitation is no longer valid' };
      }

      if (new Date(invitation.expires_at) < new Date()) {
        await supabase
          .from('family_invitations')
          .update({ status: 'expired' })
          .eq('id', invitation.id);

        return { success: false, error: 'Invitation has expired' };
      }

      // Check if user is already a member BEFORE attempting insert
      console.log('[FamilyInvitationStore] Checking for existing membership...');
      const { data: existingMember, error: checkError } = await supabase
        .from('family_members')
        .select('id')
        .eq('family_group_id', invitation.family_group_id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (checkError) {
        console.error('[FamilyInvitationStore] Error checking membership:', checkError);
      }

      if (existingMember) {
        console.log('[FamilyInvitationStore] ✓ User is already a member, skipping insert');

        // Mark invitation as accepted via RPC
        const { data: acceptResult, error: acceptError } = await supabase.rpc(
          'accept_invitation_status',
          { invitation_id_param: invitation.id }
        );

        if (acceptError) {
          console.error('[FamilyInvitationStore] Error marking invitation as accepted:', acceptError);
        } else if (acceptResult && !acceptResult.success) {
          console.warn('[FamilyInvitationStore] Failed to mark invitation as accepted:', acceptResult.error);
        }

        // Refresh family groups via the family store
        requestDeduplicator.invalidateCache();
        const familyStore = useFamilyGroupStore.getState();
        await familyStore.fetchFamilyGroups();
        await new Promise(resolve => setTimeout(resolve, 100));

        const { familyGroups } = useFamilyGroupStore.getState();
        const family = familyGroups.find(f => f.id === invitation.family_group_id);

        if (family) {
          console.log('[FamilyInvitationStore] Setting existing family as active:', family.name);
          familyStore.setActiveFamilyId(invitation.family_group_id);
          localStorage.setItem('activeFamilyId', invitation.family_group_id);
        }

        return { success: true, familyGroupId: invitation.family_group_id, alreadyMember: true };
      }

      // User is not a member, proceed with insertion
      console.log('[FamilyInvitationStore] Adding user as new member...');

      // Wait for user_profile to exist (race condition prevention)
      let profileExists = false;
      let retries = 0;
      const maxRetries = 5;

      while (!profileExists && retries < maxRetries) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('id', user.id)
          .single();

        if (profile) {
          profileExists = true;
        } else {
          console.log(`[acceptInvitation] Profile not found, retry ${retries + 1}/${maxRetries}`);
          await new Promise(resolve => setTimeout(resolve, 800));
          retries++;
        }
      }

      if (!profileExists) {
        throw new Error('Profile not created. Please try again.');
      }

      // Insert family member
      const { error: memberError } = await supabase
        .from('family_members')
        .insert([{
          family_group_id: invitation.family_group_id,
          user_id: user.id,
          role: invitation.role,
          invited_by: invitation.invited_by,
        }]);

      if (memberError) {
        if (memberError.message?.includes('duplicate key') ||
            memberError.message?.includes('family_members_family_group_id_user_id_key')) {
          console.log('[FamilyInvitationStore] Duplicate key error caught, user was added concurrently');

          const { data: acceptResult, error: acceptError } = await supabase.rpc(
            'accept_invitation_status',
            { invitation_id_param: invitation.id }
          );

          if (acceptError) {
            console.error('[FamilyInvitationStore] Error marking invitation as accepted:', acceptError);
          }

          requestDeduplicator.invalidateCache();
          const familyStore = useFamilyGroupStore.getState();
          await familyStore.fetchFamilyGroups();
          await new Promise(resolve => setTimeout(resolve, 100));

          const { familyGroups } = useFamilyGroupStore.getState();
          const family = familyGroups.find(f => f.id === invitation.family_group_id);

          if (family) {
            familyStore.setActiveFamilyId(invitation.family_group_id);
            localStorage.setItem('activeFamilyId', invitation.family_group_id);
          }

          return { success: true, familyGroupId: invitation.family_group_id, alreadyMember: true };
        }

        throw memberError;
      }

      console.log('[FamilyInvitationStore] ✓ User added as member successfully');

      // Mark invitation as accepted via RPC
      const { data: acceptResult, error: acceptError } = await supabase.rpc(
        'accept_invitation_status',
        { invitation_id_param: invitation.id }
      );

      if (acceptError) {
        console.error('[acceptInvitation] ❌ RPC error:', acceptError);
      } else if (acceptResult && !acceptResult.success) {
        console.warn('[acceptInvitation] ⚠️ Failed to mark as accepted:', acceptResult.error);
      }

      // Clear all caches
      requestDeduplicator.invalidateCache();

      console.log('[acceptInvitation] ✅ All operations complete');
      return { success: true, familyGroupId: invitation.family_group_id };
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      return { success: false, error: error.message || 'Failed to accept invitation' };
    }
  },

  getInvitationByToken: async (token: string) => {
    try {
      console.log('[getInvitationByToken] Fetching invitation for token:', token);

      const { data, error } = await supabase.rpc('get_invitation_by_token', {
        token_param: token
      });

      if (error) {
        console.error('[getInvitationByToken] RPC error:', error);
        return { invitation: null, reason: 'QUERY_ERROR' };
      }

      if (data) {
        console.log('[getInvitationByToken] ✓ Invitation found:', data.email);
        return {
          invitation: {
            id: data.id,
            family_group_id: data.family_group_id,
            email: data.email,
            role: data.role,
            status: data.status,
            expires_at: data.expires_at,
            user_exists: data.user_exists,
            token,
            family: {
              id: data.family_group_id,
              name: data.family_name,
            },
            inviter_first_name: data.inviter_first_name,
            invitation_type: data.invitation_type,
            story_id: data.story_id,
            story_title: data.story_title,
            story_photo_url: data.story_photo_url,
            story_relationship: data.story_relationship,
            protagonist_first_name: data.protagonist_first_name,
            protagonist_last_name1: data.protagonist_last_name1,
            protagonist_last_name2: data.protagonist_last_name2,
          } as FamilyInvitation,
          reason: null,
        };
      }

      // Determine why invitation wasn't found
      console.log('[getInvitationByToken] Not found, checking reason...');
      const { data: rawInvitation } = await supabase
        .from('family_invitations')
        .select('id, status, expires_at, family_group_id')
        .eq('token', token)
        .maybeSingle();

      if (!rawInvitation) {
        return { invitation: null, reason: 'TOKEN_INVALID' };
      }

      if (rawInvitation.status === 'cancelled') {
        return { invitation: null, reason: 'CANCELLED' };
      }

      if (rawInvitation.status === 'accepted') {
        return { invitation: null, reason: 'ALREADY_ACCEPTED' };
      }

      if (new Date(rawInvitation.expires_at) < new Date()) {
        return { invitation: null, reason: 'EXPIRED' };
      }

      const { data: group } = await supabase
        .from('family_groups')
        .select('id')
        .eq('id', rawInvitation.family_group_id)
        .maybeSingle();

      if (!group) {
        return { invitation: null, reason: 'GROUP_DELETED' };
      }

      return { invitation: null, reason: 'UNKNOWN' };
    } catch (error) {
      console.error('[getInvitationByToken] Exception:', error);
      return { invitation: null, reason: 'EXCEPTION' };
    }
  },
}));
